import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

function HackathonHero() {
  const canvasRefDesktop = useRef(null);
  const canvasRefMobile = useRef(null);
  const [activeSection, setActiveSection] = useState(null);
  const [targetRotation, setTargetRotation] = useState(0);
  const [buttonPosition, setButtonPosition] = useState(null);
  const buttonRefs = useRef({});

  // Utility: update canvas size with mobile-safe fallbacks
  const updateCanvasSize = (canvas) => {
    if (!canvas) return;
    const ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return;
    
    // Get container dimensions
    const container = canvas.parentElement;
    const containerWidth = container ? container.clientWidth : canvas.offsetWidth || 300;
    const containerHeight = container ? container.clientHeight : canvas.offsetHeight || 300;
    
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(containerWidth * dpr));
    canvas.height = Math.max(1, Math.floor(containerHeight * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    canvas.style.width = containerWidth + 'px';
    canvas.style.height = containerHeight + 'px';
  };

  // Animation loop effect
  useEffect(() => {
    const canvases = [canvasRefDesktop.current, canvasRefMobile.current].filter(Boolean);
    if (!canvases.length) return;

    let animationFrameId = null;
    let arcProgress = 0;
    let earthRotation = 0;

    // Draw function for canvas (moved inside useEffect to avoid dependency issues)
    const drawCanvas = (canvas) => {
      if (!canvas) return;
      const ctx = canvas.getContext && canvas.getContext('2d');
      if (!ctx) return;

      // Use canvas.width and canvas.height (not getBoundingClientRect which gives display size)
      const dpr = window.devicePixelRatio || 1;
      const canvasWidth = canvas.width / dpr;
      const canvasHeight = canvas.height / dpr;
      const centerX = canvasWidth / 2;
      const centerY = canvasHeight / 2;
      const minDim = Math.min(canvasWidth, canvasHeight);
      const radius = minDim * 0.38;

      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      // Draw Earth sphere with realistic ocean colors
      const gradient = ctx.createRadialGradient(
        centerX - radius * 0.3,
        centerY - radius * 0.3,
        radius * 0.1,
        centerX,
        centerY,
        radius
      );
      gradient.addColorStop(0, '#2A4A6E');
      gradient.addColorStop(0.5, '#1E3A5A');
      gradient.addColorStop(1, '#12263D');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fill();

      // Draw latitude/longitude grid with rotation
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(earthRotation);
      ctx.translate(-centerX, -centerY);

      ctx.strokeStyle = '#E8E8E8';
      ctx.lineWidth = 0.8;
      ctx.globalAlpha = 0.35;

      // Longitude lines
      for (let i = 0; i < 12; i++) {
        const angle = (i * Math.PI) / 6;
        ctx.beginPath();
        ctx.ellipse(
          centerX,
          centerY,
          radius * Math.abs(Math.cos(angle)),
          radius,
          0,
          0,
          Math.PI * 2
        );
        ctx.stroke();
      }

      // Latitude lines
      for (let i = 1; i < 6; i++) {
        const y = (i / 6) * radius;
        const ellipseRadius = radius * Math.sin(Math.acos(y / radius));
        ctx.beginPath();
        ctx.ellipse(centerX, centerY - y, ellipseRadius, ellipseRadius * 0.3, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.ellipse(centerX, centerY + y, ellipseRadius, ellipseRadius * 0.3, 0, 0, Math.PI * 2);
        ctx.stroke();
      }

      ctx.restore();
      ctx.globalAlpha = 1;

      // Draw Skopje marker
      const skopjeAngle = earthRotation + Math.PI * 0.25;
      const skopjeDistance = radius * 0.7;
      const skopjeX = centerX + Math.cos(skopjeAngle) * skopjeDistance * Math.cos(Math.PI * 0.2);
      const skopjeY = centerY + Math.sin(skopjeAngle) * skopjeDistance * Math.cos(Math.PI * 0.2) - radius * 0.15;
      
      const skopjeGlow = ctx.createRadialGradient(skopjeX, skopjeY, 0, skopjeX, skopjeY, 10);
      skopjeGlow.addColorStop(0, '#EFA74F');
      skopjeGlow.addColorStop(0.5, 'rgba(239, 167, 79, 0.6)');
      skopjeGlow.addColorStop(1, 'rgba(239, 167, 79, 0)');
      ctx.fillStyle = skopjeGlow;
      ctx.beginPath();
      ctx.arc(skopjeX, skopjeY, 10, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.fillStyle = '#DFC293';
      ctx.beginPath();
      ctx.arc(skopjeX, skopjeY, 3, 0, Math.PI * 2);
      ctx.fill();

      // Draw animated arcs
      const sections = [
        { id: 'requirements', angle: -45, offset: 0 },
        { id: 'challenge', angle: 45, offset: 0.25 },
        { id: 'expectations', angle: 135, offset: 0.5 },
        { id: 'evaluation', angle: 225, offset: 0.75 },
      ];

      sections.forEach(section => {
        const shouldPulse = !activeSection || activeSection === section.id;
        if (!shouldPulse) return;

        const labelAngleRad = (section.angle * Math.PI) / 180;
        const labelDistance = radius * 1.68;
        const endX = centerX + Math.cos(labelAngleRad) * labelDistance;
        const endY = centerY + Math.sin(labelAngleRad) * labelDistance;
        const progress = (arcProgress + section.offset) % 1;
        
        const controlDistance = radius * 1.35;
        const controlAngle = (skopjeAngle + labelAngleRad) / 2;
        const controlX = centerX + Math.cos(controlAngle) * controlDistance;
        const controlY = centerY + Math.sin(controlAngle) * controlDistance;
        
        const pathGradient = ctx.createLinearGradient(skopjeX, skopjeY, endX, endY);
        pathGradient.addColorStop(0, '#6A310A');
        pathGradient.addColorStop(0.5, '#EFA74F');
        pathGradient.addColorStop(1, '#6A310A');

        ctx.strokeStyle = pathGradient;
        ctx.lineWidth = activeSection === section.id ? 3 : 1.5;
        ctx.globalAlpha = activeSection === section.id ? 0.8 : 0.3;
        ctx.beginPath();
        ctx.moveTo(skopjeX, skopjeY);
        ctx.quadraticCurveTo(controlX, controlY, endX, endY);
        ctx.stroke();

        const t = progress;
        const pulseX = (1-t)*(1-t)*skopjeX + 2*(1-t)*t*controlX + t*t*endX;
        const pulseY = (1-t)*(1-t)*skopjeY + 2*(1-t)*t*controlY + t*t*endY;
        
        const pulseGradient = ctx.createRadialGradient(pulseX, pulseY, 0, pulseX, pulseY, activeSection === section.id ? 12 : 8);
        pulseGradient.addColorStop(0, '#EFA74F');
        pulseGradient.addColorStop(0.5, 'rgba(239, 167, 79, 0.6)');
        pulseGradient.addColorStop(1, 'rgba(239, 167, 79, 0)');
        
        ctx.fillStyle = pulseGradient;
        ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.arc(pulseX, pulseY, activeSection === section.id ? 12 : 8, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    // Initial sizing with mobile-safe fallback
    const resizeAll = () => {
      canvases.forEach(updateCanvasSize);
      // Draw immediately after resize
      canvases.forEach(drawCanvas);
    };
    resizeAll();
    const resizeTimeout = setTimeout(resizeAll, 250);
    window.addEventListener('resize', resizeAll);

    // Animation loop
    const animate = () => {
      arcProgress = (arcProgress + 0.01) % 1;
      
      const cur = earthRotation;
      const diff = targetRotation - cur;
      // When no section is active (targetRotation is 0), make globe rotate slowly
      if (!activeSection) {
        earthRotation = (earthRotation + 0.002) % (Math.PI * 2);
      } else {
        earthRotation = Math.abs(diff) < 0.01 ? targetRotation : cur + diff * 0.1;
      }

      canvases.forEach(drawCanvas);
      animationFrameId = requestAnimationFrame(animate);
    };

    // Start animation immediately
    animate();

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', resizeAll);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [targetRotation, activeSection]);

  // (removed dynamic date-chip padding measurement)

  const floatingLabels = [
    {
      id: 'requirements',
      text: 'Што е потребно?',
      angle: -45,
      distance: 1.5,
      rotation: 0,
      info: `За да бидеш дел од натпреварот, ти треба само креативност и желба да создадеш решение што ќе го направи секојдневието подобро и тим што ќе ја сподели истата инспирација. Фокусот не е на технологија, туку на решение кое ќе има вистинско влијание и ќе ја поттикне иновативноста — доволно е да имаш отворен ум, волја за учење и спремност да соработуваш.

Секој учесник добива можност да работи во тим од 3–5 луѓе, да истражува проблеми од реалниот живот и да креира иновативни технолошки решенија. Единствено важно е да бидеш мотивиран, проактивен и подготвен да ја претвориш инспирацијата во нешто корисно.
`
    },
    {
      id: 'challenge',
      text: 'Кој е предизвикот?',
      angle: 45,
      distance: 1.5,
      rotation: Math.PI * 0.5,
      info: `Темата на Futurathon е “Innovate for Good” – создавање идеи и решенија кои го подобруваат секојдневието и придонесуваат за подобра иднина. Учесниците ќе работат на проект по нивен избор, инспириран од реални проблеми, како што се: поедноставување на процеси, унапредување на корисничко искуство, дигитализација, автоматизација, дизајн на практични алатки, социјални импакти и модерни технологиски пристапи.Не е важно дали тимот ќе создаде прототип, мокап, концепт, модел или функционална демо верзија – најважна е идејата, нејзината креативност, применливост и начинот на презентација.`
    },
    {
      id: 'expectations',
      text: 'Што ве очекува?',
      angle: 135,
      distance: 1.5,
      rotation: Math.PI,
      info: `- Два дена полни со енергија, иновативност и тимски дух
- Работна атмосфера + забава
- Менторска поддршка
- Храна и пијалоци обезбедени од нас
- Можност да запознаете професионалци од индустрија
- Вредна награда за прво место!`
    },
    {
      id: 'evaluation',
      text: 'Што се оценува?',
      angle: 225,
      distance: 1.5,
      rotation: Math.PI * 1.5,
      info: `Се оценуваат:
- Иновација и креативност
- Техничка изводливост
- Применливост во реалниот свет
- Презентација на идејата`
    },
  ];

  const handleLabelClick = (label) => {
    const buttonEl = buttonRefs.current[label.id];
    if (buttonEl) {
      const rect = buttonEl.getBoundingClientRect();
      setButtonPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }
    
    if (activeSection === label.id) {
      setActiveSection(null);
      setTargetRotation(0);
      setButtonPosition(null);
    } else {
      setActiveSection(label.id);
      setTargetRotation(label.rotation);
    }
  };

  const activeLabel = floatingLabels.find(l => l.id === activeSection);

  // Render info text while converting lines that start with '-' into unordered lists
  const renderInfoNodes = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    const nodes = [];
    let listBuffer = [];
    const flushList = () => {
      if (listBuffer.length) {
        nodes.push(
          <ul className="list-disc list-inside mb-2" key={"ul-" + nodes.length}>
            {listBuffer.map((li, i) => (
              <li key={i}>{li}</li>
            ))}
          </ul>
        );
        listBuffer = [];
      }
    };

    lines.forEach((rawLine, idx) => {
      const line = rawLine.trim();
      if (line.startsWith('-')) {
        // remove leading dashes and possible spaces
        listBuffer.push(line.replace(/^[-\u2013\u2014]+\s*/, '').trim());
      } else if (line === '') {
        flushList();
        nodes.push(<br key={"br-" + idx} />);
      } else {
        flushList();
        nodes.push(
          <p className="mb-2" key={"p-" + idx}>
            {line}
          </p>
        );
      }
    });

    flushList();
    return nodes;
  };

  return (
    <div className="relative w-full min-h-screen overflow-x-hidden" style={{ backgroundColor: '#E4570B' }}>
      {/* Geometric grid background */}
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(#6A310A 1px, transparent 1px),
            linear-gradient(90deg, #6A310A 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      {/* Gradient overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at top left, rgba(239, 167, 79, 0.2) 0%, transparent 50%), radial-gradient(ellipse at bottom right, rgba(106, 49, 10, 0.3) 0%, transparent 50%)',
        }}
      />

      {/* Desktop: Two-column layout */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-6rem)]">
          
          {/* LEFT COLUMN - Text & Buttons */}
          <div className="flex flex-col space-y-8 lg:space-y-10">
            
            {/* Top badge */}
            {/* <div className="inline-flex items-center self-start">
              <div
                className="px-6 py-3 rounded-full text-2xl font-normal"
                style={{
                  backgroundColor: '#6A310A',
                  color: '#DFC293',
                  boxShadow: '0 0 20px rgba(106, 49, 10, 0.5)',
                  fontFamily: 'Sagewold, system-ui, sans-serif',
                }}
              >
                БЕСТовски Хакатон 2025
              </div>
            </div> */}

            {/* Large glowing headline */}
            <h1
              className="text-4xl sm:text-6xl lg:text-7xl font-bold leading-tight"
              style={{
                color: '#DFC293',
                textShadow: '0 0 18px rgba(255,255,255,0.12), 0 0 30px rgba(223, 194, 147, 0.45), 0 0 60px rgba(239, 167, 79, 0.25)',
              }}
            >
              Futurathon
            </h1>

            {/* Subheading */}
            <div className="space-y-4 pr-0 lg:pr-0">

              <p
                className="text-lg sm:text-xl lg:text-2xl leading-relaxed max-w-md"
                style={{ color: '#DFC299', opacity: 0.95 }}
              >
                Дводневен студентски натпревар посветен на создавање идеи и решенија кои ја подобруваат иднината. Настан каде што технологијата, дизајнот и креативноста се спојуваат во уникатно искуство исполнето со тимска работа, енергија и иновации. Тимовите работат на реален предизвик и имаат целосна слобода да создадат свое решение.<br/>
              
              <span className='block mt-3 text-lg sm:text-xl lg:text-2xl'>Создади ја иднината заедно!
                <br/>Со помош на технологија, сплотен тим и креативен ум сè е можно!
              </span>
              </p>
            </div>

            {/* Info cards (chips) - stacked vertically for clarity */}
            <div className="flex flex-col gap-3">
              {[
                {text: 'Локација: IT Labs, Скопје', bg: '#749269', link: 'https://google.com/maps?um=1&ie=UTF-8&fb=1&gl=mk&sa=X&geocode=Ka0Ckh-2FVQTMUmz0xUfMZKe&daddr=11th+October+St.+25,+Skopje+1000' },
                { text: 'Датум: 20 и 21 декември 2025', bg: '#EFA74F' },
                { text: 'Рок за пријавување: 10 декември 2025', bg: '#749269' },
                { text: 'Цел: Подобрување на секојдневниот живот', bg: '#EFA74F' },
              ].map((card, idx) => {
                const cardContent = (
                  <div
                    className="px-5 py-2.5 rounded-full text-lg sm:text-xl font-medium border-2 w-full max-w-md"
                    style={{
                      backgroundColor: card.bg,
                      borderColor: '#6A310A',
                      color: (card.bg === '#EFA74F' || card.bg === '#FFD36B') ? '#6A310A' : '#DFC299',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      cursor: card.link ? 'pointer' : 'default',
                    }}
                  >
                    {card.text}
                  </div>
                );

                return card.link ? (
                  <a key={idx} href={card.link} target="_blank" rel="noopener noreferrer" className="block">
                    {cardContent}
                  </a>
                ) : (
                  <div key={idx}>{cardContent}</div>
                );
              })}
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <a href="https://docs.google.com/forms/d/e/1FAIpQLSf6p-Cd071IJkln9JH2ZH535XMKNSCfqu1_2Koq6JIxkrnLhA/viewform" target="_blank" rel="noopener noreferrer">
              <button
                className="px-8 py-4 rounded-lg text-xl font-semibold transition-all duration-300 hover:scale-105"
                style={{
                  backgroundColor: '#EFA74F',
                  color: '#6A310A',
                  boxShadow: '0 0 25px rgba(239, 167, 79, 0.45), 0 4px 15px rgba(0, 0, 0, 0.2)',
                }}
              >
                Пријави се
              </button>
              </a>
              <Link to="/faq">
              <button
                className="px-8 py-4 rounded-lg text-xl font-semibold border-2 transition-all duration-300 hover:bg-opacity-10 hover:bg-white"
                style={{ borderColor: '#749269', color: '#EFA74F', backgroundColor: 'transparent' }}
              >
                Повеќе информации
              </button>
              </Link>
            </div>
          </div>

          {/* RIGHT COLUMN - 3D Globe (Desktop only) */}
          <div className="hidden lg:flex justify-center items-center relative">
            <div className="relative w-full max-w-lg aspect-square">
              {/* Canvas for globe */}
              <canvas
                ref={canvasRefDesktop}
                className="w-full h-full"
                style={{ 
                  filter: 'drop-shadow(0 0 40px rgba(239, 167, 79, 0.3))',
                  display: 'block'
                }}
              />

              {/* Floating labels */}
              {floatingLabels.map((label, idx) => {
                const angleRad = (label.angle * Math.PI) / 180;
                const x = 50 + Math.cos(angleRad) * label.distance * 50;
                const y = 50 + Math.sin(angleRad) * label.distance * 50;
                const isActive = activeSection === label.id;
                
                return (
                  <button
                    key={idx}
                    ref={(el) => (buttonRefs.current[label.id] = el)}
                    onClick={() => handleLabelClick(label)}
                    className="absolute px-5 py-3 rounded-lg text-xl font-medium border whitespace-nowrap transition-all duration-300 hover:scale-110 cursor-pointer"
                    style={{
                      left: `${x}%`,
                      top: `${y}%`,
                      transform: 'translate(-50%, -50%)',
                        backgroundColor: isActive ? '#C28A2E' : '#6A310A',
                      color: '#DFC293',
                        borderColor: isActive ? '#DFC293' : '#749269',
                      borderWidth: isActive ? '2px' : '1px',
                      boxShadow: isActive 
                        ? '0 0 20px rgba(239, 167, 79, 0.6), 0 4px 12px rgba(0, 0, 0, 0.25)'
                        : '0 4px 12px rgba(0, 0, 0, 0.25)',
                      visibility: activeSection === label.id ? 'hidden' : 'visible',
                    }}
                  >
                    <span className="inline-flex items-center mr-2" aria-hidden>
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 19l7-7-7-7" />
                        <path d="M5 12h14" />
                      </svg>
                    </span>
                    <span>{label.text}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* MOBILE: Globe centered below text */}
          <div className="flex lg:hidden justify-center items-center relative mt-8 px-2 overflow-visible">
            <div className="relative w-full max-w-full aspect-square flex items-center justify-center" style={{ maxWidth: '400px', paddingTop: '80px', paddingBottom: '80px', paddingLeft: '10px', paddingRight: '10px' }}>
              <canvas
                ref={canvasRefMobile}
                className="w-full h-full absolute inset-0"
                style={{ filter: 'drop-shadow(0 0 40px rgba(239, 167, 79, 0.3))', margin: 'auto' }}
              />

              {/* Floating labels for mobile */}
              {floatingLabels.map((label, idx) => {
                const angleRad = (label.angle * Math.PI) / 180;
                // Reduce distance to keep buttons within screen with vertical padding
                const mobileLabelDistance = label.distance * 0.75;
                const x = 50 + Math.cos(angleRad) * mobileLabelDistance * 50;
                const y = 50 + Math.sin(angleRad) * mobileLabelDistance * 50;
                const isActive = activeSection === label.id;
                
                return (
                  <button
                    key={idx}
                    ref={(el) => (buttonRefs.current[label.id] = el)}
                    onClick={() => handleLabelClick(label)}
                    className="absolute px-3 py-2 rounded-lg text-sm font-medium border whitespace-nowrap transition-all duration-300 hover:scale-110 cursor-pointer"
                    style={{
                      left: `${x}%`,
                      top: `${y}%`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: isActive ? '#C28A2E' : '#6A310A',
                      color: '#DFC293',
                      borderColor: isActive ? '#DFC293' : '#749269',
                      borderWidth: isActive ? '2px' : '1px',
                      boxShadow: isActive 
                        ? '0 0 20px rgba(239, 167, 79, 0.6), 0 4px 12px rgba(0, 0, 0, 0.25)'
                        : '0 4px 12px rgba(0, 0, 0, 0.25)',
                      visibility: activeSection === label.id ? 'hidden' : 'visible',
                      maxWidth: '85vw',
                    }}
                    >
                    <span className="inline-flex items-center mr-1.5" aria-hidden>
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 19l7-7-7-7" />
                        <path d="M5 12h14" />
                      </svg>
                    </span>
                    <span>{label.text}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Modal overlay - appears when section is active */}
          {activeSection && buttonPosition && (
            <div 
              className="fixed inset-0 flex items-center justify-center px-4 py-8"
              style={{ 
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                animation: 'fadeIn 0.3s ease-out',
                zIndex: 9999
              }}
              onClick={() => {
                setActiveSection(null);
                setTargetRotation(0);
                setButtonPosition(null);
              }}
            >
              <div
                className="relative max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6 sm:p-8 rounded-xl border-2 backdrop-blur-sm"
                style={{
                  backgroundColor: 'rgba(106, 49, 10, 0.95)',
                  borderColor: '#EFA74F',
                  boxShadow: '0 0 40px rgba(239, 167, 79, 0.5), 0 8px 30px rgba(0, 0, 0, 0.4)',
                  animation: 'expandFromButton 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transformOrigin: 'center',
                  '--btn-top': `${buttonPosition.top}px`,
                  '--btn-left': `${buttonPosition.left}px`,
                  '--btn-width': `${buttonPosition.width}px`,
                  '--btn-height': `${buttonPosition.height}px`,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close X button */}
                <button
                  onClick={() => {
                    setActiveSection(null);
                    setTargetRotation(0);
                    setButtonPosition(null);
                  }}
                  className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110"
                  style={{
                    backgroundColor: '#749269',
                    color: '#DFC293',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  }}
                  aria-label="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>

                <h3
                  className="text-2xl sm:text-3xl font-bold mb-4 pr-12"
                  style={{ color: '#EFA74F' }}
                >
                  {floatingLabels.find(l => l.id === activeSection)?.text}
                </h3>
                <div
                  className="text-lg sm:text-xl leading-relaxed"
                  style={{ color: '#DFC293', hyphens: 'auto' }}
                >
                  {activeLabel?.id === 'evaluation' ? (
                    <>
                      <div style={{ color: '#EFA74F', fontWeight: 600 }}>{activeLabel.info.split('\n')[0]}</div>
                      <div className="mt-2">{renderInfoNodes(activeLabel.info.split('\n').slice(1).join('\n'))}</div>
                    </>
                  ) : (
                    <>{renderInfoNodes(activeLabel?.info)}</>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes expandFromButton {
          0% {
            opacity: 0;
            position: fixed;
            top: var(--btn-top);
            left: var(--btn-left);
            width: var(--btn-width);
            height: var(--btn-height);
            transform: translate(0, 0) scale(1);
            border-radius: 0.5rem;
          }
          100% {
            opacity: 1;
            position: relative;
            top: auto;
            left: auto;
            width: 100%;
            height: auto;
            transform: translate(0, 0) scale(1);
            border-radius: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
}

export default HackathonHero;
